import axios from "axios";
import { useTranslate } from "hooks/useTranslate";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosPostToken } from "utils/APIHelper";
import { apiFailureMessage, messages } from "utils/CommonConstants";
import { handleUnauthorizedAccess } from "utils/CommonFunctions";
import { responseTypes } from "utils/Types";
import { TOKEN_URL } from "utils/URLConstants";

let instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

function isUnauthorizedError(error: any) {
  const {
    response: { status }
  } = error;
  return status === responseTypes.unauthorized;
}

export async function renewToken() {
  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) throw new Error("refresh token does not exist");
  const response = await axiosPostToken(TOKEN_URL);
  return response;
}

function HandleApiFailure() {
  const navigate = useNavigate();
  const translate = useTranslate();
  let refreshingFunc: any = undefined;
  let counter = 0;

  useEffect(() => {
    instance.interceptors.response.use(
      (res) => res,
      async (error) => {
        if (
          error?.code === "ERR_NETWORK" ||
          error?.response?.status === responseTypes.forbidden ||
          (error?.response?.status >= responseTypes.internal_error &&
            error?.response?.status < responseTypes.other_error)
        ) {
          return {
            response: {
              status: error?.response?.status || responseTypes.internal_error,
              data: {
                errors: [
                  {
                    title: 0,
                    detail:
                      error?.response?.status === responseTypes.forbidden
                        ? translate("user.restrictAccess")
                        : apiFailureMessage
                  }
                ]
              }
            }
          };
        }

        const split = error?.config?.url?.split("/");
        const isTokenAPI = split?.length && split.includes("token.oauth2");
        if (
          error?.response?.status === responseTypes.not_found &&
          ((split?.includes("plans") && !split?.includes("sections")) || split?.includes("cards"))
        ) {
          navigate(`/`, {
            state: {
              hasNoAccess: true,
              message: messages.planNotFound
            }
          });
        }

        if (counter > 1) handleUnauthorizedAccess();
        const token = localStorage.getItem("id_token");
        if (!token || isUnauthorizedError(error) || error?.response?.data?.error === "invalid_grant") {
          try {
            if (isTokenAPI) counter++;
            const originalConfig = error?.config;
            // the trick here, that `refreshingFunc` is global, e.g. 2 expired requests will get the same function pointer and await same function.
            if (!refreshingFunc || (counter < 3 && isTokenAPI)) refreshingFunc = renewToken();
            const response = await refreshingFunc;
            const { access_token, refresh_token } = response.data;
            counter = 0;
            localStorage.setItem("id_token", access_token);
            if (refresh_token !== undefined && refresh_token) {
              localStorage.setItem("refresh_token", refresh_token);
            }
            originalConfig.headers.Authorization = `Bearer ${access_token}`;

            try {
              if (!originalConfig.url.split("/").includes("token.oauth2")) {
                return await instance.request(originalConfig);
              }
            } catch (innerError) {
              // if original req failed with 401 again - it means server returned not valid token for refresh request
              if (isUnauthorizedError(innerError)) {
                throw innerError;
              }
            }
          } catch (err) {
            handleUnauthorizedAccess();
          } finally {
            refreshingFunc = undefined;
          }
        } else {
          return error;
        }
      }
    );
  }, [navigate]);

  return <></>;
}

export default instance;
export { HandleApiFailure };
